/**
 * "O que fazer agora" das Comissoes: sugestoes derivadas dos numeros do
 * recorte, ordenadas por urgencia.
 *
 * Decisoes nao obvias:
 *  1. Cada insight e uma FUNCAO PURA que devolve `null` quando o dado nao o
 *     sustenta. Nada e inventado para preencher a lista: com tudo em ordem a
 *     tela diz isso em um bloco so.
 *  2. Todo texto carrega o numero que o justifica. Insight sem numero e
 *     opiniao.
 *  3. Prioridade fixa por tipo (0 = mais urgente): primeiro o que deixa de
 *     entrar dinheiro (comissao acumulando sem modelo, fatura em aberto),
 *     depois o que muda o valor (chargeback, percentual alterado, venda
 *     assistida a conferir), por fim leitura de tendencia.
 *  4. A ACAO e tipada e a pagina executa: filtrar o extrato (e rolar ate
 *     ele), rolar ate uma secao, filtrar por empresa ou abrir o cadastro.
 *  5. Nenhum insight sugere agir ANTES do fato (lembrete de renovacao,
 *     cobranca preventiva): o motor so entra depois do abandono ou do
 *     vencimento, e so dai vem a comissao (decisao do dono, 24/09/2026).
 */

import type { BadgeTone, IconName } from '../../components/ui';
import { formatBRLFromCents, formatCount, formatPercentFromBps } from '../../lib/format';
import type { CompanySummary } from '../../types';
import { participacaoBps, variar } from './comparar';
import type { CommissionsOverview, ConversionFilters } from './types';
import { plural } from './vocabulario';

export type AcaoDoInsight =
  | { readonly kind: 'extrato'; readonly rotulo: string; readonly filtros: Partial<ConversionFilters> }
  | { readonly kind: 'ancora'; readonly rotulo: string; readonly id: string }
  | { readonly kind: 'rota'; readonly rotulo: string; readonly rota: string; readonly aba: AbaDaEmpresa | null };

export interface Insight {
  readonly id: string;
  /** 0 = mais urgente. */
  readonly prioridade: number;
  readonly tone: BadgeTone;
  readonly icone: IconName;
  readonly titulo: string;
  readonly texto: string;
  readonly acao: AcaoDoInsight | null;
}

const MAXIMO_DE_INSIGHTS = 5;
/** Variacao da comissao que merece leitura (20%). */
const VARIACAO_RELEVANTE_BPS = 2_000;
/** Comissao revertida acima disto da comissao ativa vira alerta critico. */
const REVERSAO_CRITICA_BPS = 500;
/** Participacao do comercial que vale destacar. */
const PESO_DO_COMERCIAL_BPS = 2_000;
/** Uma empresa com mais que isto da comissao e concentracao de risco. */
const CONCENTRACAO_BPS = 5_000;

/** Parametro da aba no detalhe da empresa (o mesmo que admin-empresas le da URL). */
export const PARAM_ABA_DA_EMPRESA = 'emp.aba';

/** Aba do detalhe onde a administracao altera percentual, cobranca e politica de estorno. */
export type AbaDaEmpresa = 'comissao';

/** Caminho do detalhe da empresa; sem `aba`, abre no Resumo. */
export function cadastroDaEmpresa(companyId: string): string {
  return `/admin/empresas/${encodeURIComponent(companyId)}`;
}

/**
 * Detalhe da empresa ja na aba "Comissão e cobrança", preservando os
 * parametros atuais (periodo). Caminho COMPLETO com a query: use com
 * `LinkButton keepSearch={false}`, que senao colaria a query no pathname.
 */
export function abaComissaoDaEmpresa(companyId: string, searchAtual: string): string {
  const params = new URLSearchParams(searchAtual);
  params.set(PARAM_ABA_DA_EMPRESA, 'comissao');
  return `${cadastroDaEmpresa(companyId)}?${params.toString()}`;
}

interface Contexto {
  readonly visao: CommissionsOverview;
  /** Cadastro VIGENTE (useCompany): percentual e modelo podem ter sido alterados agora. */
  readonly empresas: readonly CompanySummary[];
}

type Gerador = (c: Contexto) => Insight | null;

function nomes(lista: readonly string[], maximo = 2): string {
  const primeiros = lista.slice(0, maximo);
  const resto = lista.length - primeiros.length;
  return resto > 0 ? `${primeiros.join(', ')} e mais ${formatCount(resto)}` : primeiros.join(' e ');
}

const acumulandoSemModelo: Gerador = ({ visao, empresas }) => {
  const rows = visao.companies.filter((r) => r.pendingCommissionCents > 0 && (empresas.find((e) => e.id === r.companyId)?.billingModel ?? r.billingModel) === 'a_definir');
  const primeira = rows[0];
  if (primeira === undefined) {
    return null;
  }
  const total = rows.reduce((acc, r) => acc + r.pendingCommissionCents, 0);
  return {
    id: 'acumulando-sem-modelo',
    prioridade: 0,
    tone: 'warning',
    icone: 'alerta',
    titulo: `${formatBRLFromCents(total)} a faturar sem modelo de cobrança`,
    texto: `${nomes(rows.map((r) => r.companyName))} ${plural(rows.length, 'está', 'estão')} com o modelo "a definir": a comissão acumula e ninguém é cobrado até a decisão.`,
    acao: { kind: 'rota', rotulo: 'Definir cobrança', rota: cadastroDaEmpresa(primeira.companyId), aba: 'comissao' },
  };
};

const faturadasEmAberto: Gerador = ({ visao }) => {
  const b = visao.summary.byStatus.faturada;
  if (b.count === 0) {
    return null;
  }
  return {
    id: 'faturadas-em-aberto',
    prioridade: 1,
    tone: 'warning',
    icone: 'relogio',
    titulo: `${formatBRLFromCents(b.commissionCents)} faturados e ainda não pagos`,
    texto: `${formatCount(b.count)} ${plural(b.count, 'conversão está', 'conversões estão')} em fatura emitida sem pagamento da empresa. Confira antes de emitir a próxima.`,
    acao: { kind: 'extrato', rotulo: 'Ver faturadas', filtros: { status: 'faturada' } },
  };
};

const chargebackReverteu: Gerador = ({ visao }) => {
  const s = visao.summary;
  if (s.reversedCommissionCents === 0) {
    return null;
  }
  const peso = participacaoBps(s.reversedCommissionCents, s.commissionCents + s.reversedCommissionCents);
  const tipos = [s.refunds.reembolso, s.refunds.reembolso_parcial, s.refunds.chargeback];
  const mantidas = tipos.reduce((acc, b) => acc + b.count - b.reversedCount, 0);
  const preservada = tipos.reduce((acc, b) => acc + b.keptCommissionCents, 0);
  return {
    id: 'chargeback-reverteu',
    prioridade: 1,
    tone: peso !== null && peso >= REVERSAO_CRITICA_BPS ? 'critical' : 'warning',
    icone: 'alerta',
    titulo: `Estornos reverteram ${formatBRLFromCents(s.reversedCommissionCents)} de comissão${peso === null ? '' : ` (${formatPercentFromBps(peso)})`}`,
    texto:
      mantidas > 0
        ? `${formatCount(s.reversedCount)} ${plural(s.reversedCount, 'conversão saiu', 'conversões saíram')} da apuração. Outras ${formatCount(mantidas)} com estorno ficaram com a comissão mantida (${formatBRLFromCents(preservada)}): a política da empresa não desfaz aquele tipo de estorno (no padrão, reembolso não reverte; reembolso parcial nunca reverte).`
        : `${formatCount(s.reversedCount)} ${plural(s.reversedCount, 'conversão saiu', 'conversões saíram')} da apuração pela política de estorno da empresa.`,
    acao: { kind: 'extrato', rotulo: 'Ver revertidas', filtros: { refund: 'revertida' } },
  };
};

const percentualAlterado: Gerador = ({ visao, empresas }) => {
  const alteradas = visao.companies
    .map((r) => ({ r, atual: empresas.find((e) => e.id === r.companyId)?.feeBps ?? r.feeBps }))
    .filter(({ r, atual }) => r.conversionCount + r.reversedCount > 0 && !r.feeBpsInPeriod.every((fee) => fee === atual));
  const primeira = alteradas[0];
  if (primeira === undefined) {
    return null;
  }
  const antigos = primeira.r.feeBpsInPeriod.map(formatPercentFromBps).join(' e ');
  return {
    id: 'percentual-alterado',
    prioridade: 2,
    tone: 'info',
    icone: 'editar',
    titulo:
      alteradas.length === 1
        ? `Percentual de ${primeira.r.companyName} alterado para ${formatPercentFromBps(primeira.atual)}`
        : `Percentual alterado em ${formatCount(alteradas.length)} empresas`,
    texto: `Vale para as próximas vendas. As ${formatCount(primeira.r.conversionCount + primeira.r.reversedCount)} do período continuam com ${antigos}, congelados (ADR-001); a projeção do mês já usa o novo percentual.`,
    acao: { kind: 'rota', rotulo: 'Ver cadastro', rota: cadastroDaEmpresa(primeira.r.companyId), aba: 'comissao' },
  };
};

const vendaAssistidaAConferir: Gerador = ({ visao }) => {
  const a = visao.summary.comercial.assisted;
  if (a.count === 0) {
    return null;
  }
  return {
    id: 'venda-assistida',
    prioridade: 2,
    tone: 'accent',
    icone: 'editar',
    titulo: `${formatCount(a.count)} ${plural(a.count, 'venda assistida registrada', 'vendas assistidas registradas')} à mão (${formatBRLFromCents(a.recoveredAmountCents)})`,
    texto: 'Entraram sem webhook (Pix manual, outro link). Confira a referência do pedido de cada uma antes de faturar: é ela que casa um estorno depois.',
    acao: { kind: 'extrato', rotulo: 'Conferir', filtros: { closedBy: 'venda_assistida' } },
  };
};

const tendencia: Gerador = ({ visao }) => {
  if (visao.previous === null) {
    return null;
  }
  const v = variar(visao.summary.commissionCents, visao.previous.commissionCents);
  if (v.relativaBps === null || Math.abs(v.relativaBps) < VARIACAO_RELEVANTE_BPS) {
    return null;
  }
  const queda = v.relativaBps < 0;
  return {
    id: queda ? 'queda' : 'alta',
    prioridade: queda ? 2 : 4,
    tone: queda ? 'warning' : 'good',
    icone: 'grafico',
    titulo: `Comissão ${queda ? 'caiu' : 'subiu'} ${formatPercentFromBps(Math.abs(v.relativaBps))} contra o período anterior`,
    texto: `De ${formatBRLFromCents(visao.previous.commissionCents)} para ${formatBRLFromCents(visao.summary.commissionCents)}. O resumo por empresa mostra quem ${queda ? 'puxou a queda' : 'puxou a alta'}.`,
    acao: { kind: 'ancora', rotulo: 'Ver por empresa', id: 'resumo-por-empresa' },
  };
};

const pesoDoComercial: Gerador = ({ visao }) => {
  const c = visao.summary.comercial;
  const peso = participacaoBps(c.commissionCents, visao.summary.commissionCents);
  if (c.count === 0 || peso === null || peso < PESO_DO_COMERCIAL_BPS) {
    return null;
  }
  return {
    id: 'peso-do-comercial',
    prioridade: 3,
    tone: 'brand',
    icone: 'usuarios',
    titulo: `${formatPercentFromBps(peso)} da comissão veio de vendas fechadas pelo comercial`,
    texto: `A régua tocou, o lead respondeu e a vendedora fechou: ${formatCount(c.count)} ${plural(c.count, 'venda', 'vendas')} (${formatBRLFromCents(c.amountCents)}) com o crédito no toque do motor (ADR-001 E5).`,
    acao: { kind: 'ancora', rotulo: 'Ver vendas do comercial', id: 'vendas-pelo-comercial' },
  };
};

const concentracao: Gerador = ({ visao }) => {
  if (visao.companies.length < 2) {
    return null;
  }
  const maior = visao.companies[0];
  const peso = maior === undefined ? null : participacaoBps(maior.commissionCents, visao.summary.commissionCents);
  if (maior === undefined || peso === null || peso < CONCENTRACAO_BPS) {
    return null;
  }
  return {
    id: 'concentracao',
    prioridade: 3,
    tone: 'info',
    icone: 'empresa',
    titulo: `${maior.companyName} responde por ${formatPercentFromBps(peso)} da comissão`,
    texto: 'Quanto mais concentrado, mais uma pausa dessa empresa pesa no faturamento. O ranking mostra as cinco maiores.',
    acao: { kind: 'ancora', rotulo: 'Ver ranking', id: 'top-empresas' },
  };
};

const GERADORES: readonly Gerador[] = [acumulandoSemModelo, faturadasEmAberto, chargebackReverteu, percentualAlterado, vendaAssistidaAConferir, tendencia, pesoDoComercial, concentracao];

export function gerarInsights(visao: CommissionsOverview, empresas: readonly CompanySummary[]): readonly Insight[] {
  return GERADORES.map((g) => g({ visao, empresas }))
    .filter((i): i is Insight => i !== null)
    .sort((a, b) => a.prioridade - b.prioridade)
    .slice(0, MAXIMO_DE_INSIGHTS);
}
